'use client';

/**
 * HorizontalSlider Component
 *
 * A reusable horizontal slider component built on top of shadcn carousel (embla-carousel).
 * Features smooth drag scrolling, gradient overlays, and customizable styling.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '@ui/lib/utils';

import { Carousel, type CarouselApi, CarouselContent, CarouselItem } from '../../ui/carousel';

export interface HorizontalSliderProps {
  /**
   * Content to be displayed in the slider
   */
  children: React.ReactNode;

  /**
   * Additional CSS classes for the container
   */
  className?: string;

  /**
   * Additional CSS classes for the carousel content wrapper
   */
  contentClassName?: string;

  /**
   * Additional CSS classes for each slider item
   */
  itemClassName?: string;

  /**
   * Gap between items (default: 'gap-4')
   */
  gap?: string;

  /**
   * Minimum height of the slider container
   */
  minHeight?: string;

  /**
   * Whether to show gradient overlays on the sides
   * @default true
   */
  showGradients?: boolean;

  /**
   * Width of the gradient overlays
   * @default 'w-10'
   */
  gradientWidth?: string;

  /**
   * Callback when scroll position changes
   */
  onScroll?: () => void;

  /**
   * Whether to enable drag scrolling
   * @default true
   */
  enableDrag?: boolean;

  /**
   * Whether to enable mouse wheel scrolling
   * @default true
   */
  enableWheel?: boolean;
}

/**
 * HorizontalSlider component for displaying horizontally scrollable content
 *
 * @example
 * ```tsx
 * <HorizontalSlider>
 *   <div>Item 1</div>
 *   <div>Item 2</div>
 *   <div>Item 3</div>
 * </HorizontalSlider>
 * ```
 */
export const HorizontalSlider: React.FC<HorizontalSliderProps> = ({
  children,
  className,
  contentClassName,
  itemClassName,
  gap = 'gap-4',
  minHeight,
  showGradients = true,
  gradientWidth = 'w-10',
  onScroll,
  enableDrag = true,
  enableWheel = true
}) => {
  const [api, setApi] = useState<CarouselApi>();
  const leftGradientRef = useRef<HTMLDivElement>(null);
  const rightGradientRef = useRef<HTMLDivElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);

  // Update gradient visibility based on scroll position
  const updateGradients = useCallback(() => {
    if (!showGradients || !leftGradientRef.current || !rightGradientRef.current || !api) return;

    const canScrollPrev = api.canScrollPrev();
    const canScrollNext = api.canScrollNext();

    leftGradientRef.current.style.opacity = canScrollPrev ? '1' : '0';
    rightGradientRef.current.style.opacity = canScrollNext ? '1' : '0';
  }, [api, showGradients]);

  // Listen to carousel API changes
  useEffect(() => {
    if (!api) return;

    const updateState = () => {
      updateGradients();
      onScroll?.();
    };

    updateState();
    api.on('select', updateState);
    api.on('reInit', updateState);
    api.on('scroll', updateState);

    return () => {
      api.off('select', updateState);
      api.off('reInit', updateState);
      api.off('scroll', updateState);
    };
  }, [api, onScroll, updateGradients]);

  // Handle window resize and initial render
  useEffect(() => {
    const handleResize = () => {
      updateGradients();
    };

    // Initial check
    const timer = setTimeout(() => {
      updateGradients();
    }, 0);

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timer);
    };
  }, [updateGradients]);

  // Handle mouse wheel scrolling
  useEffect(() => {
    if (!enableWheel || !carouselRef.current) return;

    const container = carouselRef.current;
    const handleWheel = (e: WheelEvent) => {
      if (e.deltaY === 0) return;

      // Check if we're hovering over the scroll container
      const rect = container.getBoundingClientRect();
      const isHovering =
        e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom;

      if (!isHovering) return;

      e.preventDefault();
      if (e.deltaY > 0) {
        api?.scrollNext();
      } else {
        api?.scrollPrev();
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [api, enableWheel]);

  // eslint-disable-next-line react-x/no-children-to-array -- necessary for carousel item mapping
  const childrenArray = React.Children.toArray(children);

  return (
    <div className={cn('relative', className)}>
      {/* Left gradient overlay */}
      {showGradients && (
        <div
          ref={leftGradientRef}
          className={cn(
            'absolute left-0 top-0 h-full bg-gradient-to-r from-background to-transparent z-10 pointer-events-none transition-opacity duration-300 opacity-0',
            gradientWidth
          )}
        />
      )}

      {/* Carousel container */}
      <div ref={carouselRef} className="pb-4">
        <Carousel
          setApi={setApi}
          opts={{
            align: 'start',
            dragFree: enableDrag,
            containScroll: 'trimSnaps',
            watchDrag: enableDrag
          }}
          className="w-full"
        >
          <CarouselContent className={cn(minHeight && `min-h-[${minHeight}]`, gap, contentClassName)}>
            {childrenArray.map((child, index) => (
              // eslint-disable-next-line react-x/no-array-index-key -- children are stable and won't reorder
              <CarouselItem key={index} className={cn('pl-4 basis-auto', itemClassName)}>
                {child}
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      </div>

      {/* Right gradient overlay */}
      {showGradients && (
        <div
          ref={rightGradientRef}
          className={cn(
            'absolute right-0 top-0 h-full bg-gradient-to-l from-background to-transparent z-10 pointer-events-none transition-opacity duration-300 opacity-0',
            gradientWidth
          )}
        />
      )}
    </div>
  );
};
